import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
  WritableSignal,
  Signal,
  HostListener, computed, effect
} from '@angular/core';
import { HeaderSophiaComponent } from "../header-sophia/header-sophia.component";
import { MatIcon } from "@angular/material/icon";
import { PagerComponent } from "../../../../shared/components/pager/pager.component";
import { TablaAtencionesComponent } from "./components/tabla-atenciones/tabla-atenciones.component";
import { ActivatedRoute } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { firstValueFrom, map, Observable } from "rxjs";
import {
  Action, ActionMenuCoords, ButtonEmit,
  HistoricoAtencionesResponse,
  HistoryItem, ItemCareList,
  OpcionesHistorialAtenciones
} from "@shared/interfaces/paciente.interface";
import { DatePipe, NgIf, NgOptimizedImage } from "@angular/common";
import { ModalHistory360Component } from "../../../../shared/components/modal-history-360/modal-history-360.component";
import { MatDialog } from "@angular/material/dialog";
import { Historial360 } from "../../../../interfaces/historial-360.interface";
import { ResponseApiModel } from "@shared/interfaces/api-response.model";
import { HistorialService, PacienteService, UtilServiceService } from "@shared/services/index";

@Component({
  selector: 'soph-paciente',
  standalone: true,
  imports: [
    HeaderSophiaComponent,
    MatIcon,
    PagerComponent,
    TablaAtencionesComponent,
    DatePipe,
    NgOptimizedImage
  ],
  templateUrl: './paciente.component.html',
  styleUrl: './paciente.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PacienteComponent implements OnInit {

  private readonly route: ActivatedRoute = inject( ActivatedRoute );
  private readonly matDialog: MatDialog = inject( MatDialog );

  private readonly patientService: PacienteService = inject( PacienteService );
  private readonly historialService: HistorialService = inject( HistorialService );
  private readonly utils: UtilServiceService = inject( UtilServiceService );

  public patientId: Signal< string | null | undefined > = toSignal( this.processRouteInfo() );
  public history: WritableSignal< HistoryItem[] > = signal([]);
  public page: WritableSignal< number > = signal( 1 );
  public total: WritableSignal< number > = signal( 0 );
  public pageSize: WritableSignal< number > = signal( 12 );

  public actions: WritableSignal< null | Action[] > = signal( null );
  public position: WritableSignal< null | ActionMenuCoords > = signal( null );
  public isMenuOpen: WritableSignal< string > = signal('');
  public dataHistorial360: WritableSignal< Historial360 | null > = signal( null );

  public care: Signal< ItemCareList > = computed(() => this.history()[0] ? this.prepareHeaderInfo( this.history()[0] ) : {} as ItemCareList );
  public hasHistory360 = computed(() => !!this.dataHistorial360() );

  @HostListener('document:click', ['$event.target'])
  onClickOutside(targetElement: HTMLElement) {

    if ( this.actions() === null ) return;

    const tableElement = document.querySelector('.tp-wrapper');
    if ( !tableElement ) return;

    const dropdown = tableElement.querySelector('.dropdown-menu');
    const clickedInsideDropdown = dropdown?.contains(targetElement);
    const clickedOnButton = targetElement.closest('.ver-documentos-btn');
    if ( !clickedInsideDropdown && !clickedOnButton ) {
      this.actions.set( null );
      this.isMenuOpen.set('');
    }
  }

  ngOnInit(): void {
    if ( typeof this.patientId() === 'string' ) {
      this.getPatientHistory();
    }
  }

  private processRouteInfo(): Observable< string | null > {
    return this.route.paramMap.pipe( map( params => params.get('idPaciente' )));
  }

  private getPatientHistory( updatePage: boolean = false ) {
    const options: OpcionesHistorialAtenciones = {
      size: this.pageSize(),
      page: this.page(),
    }
    return this.patientService.listarHistorialAtenciones( this.patientId()!, options ).pipe(
      map(( response: HistoricoAtencionesResponse ) => {
        const { items, total } = response.data;
        if ( !updatePage ) this.total.set( total );
        return items;
      })
    ).subscribe( ( items ) => this.history.update( () => [ ...items ] ));
  }

  private async setModal360( idAgendamiento: string ) {
    const subscription = this.historialService.getHistorial_360( idAgendamiento ).pipe(
      map(( response: ResponseApiModel< Historial360 >) => response.data )
    );
    this.dataHistorial360.set( await firstValueFrom( subscription ));
  }

  private prepareHeaderInfo( item: HistoryItem ): ItemCareList {
    const { tipoAtencion, finAtencion, paciente, atenciones } = item;
    const { nombres, apellidos, numeroDocumento } = paciente;
    const { idAtencion, estado, idAgendamiento } = atenciones[0];
    this.setModal360( idAgendamiento );
    return {
      completeName: `${ nombres } ${ apellidos }`,
      date: new Date( finAtencion ),
      type: tipoAtencion,
      status: estado,
      idAtencion,
      idAgendamiento,
      actions: [],
      historiaClinica: numeroDocumento
    }
  }

  hasToShowMenu( event?: ButtonEmit | null ) {
    if ( event ) {
      this.isMenuOpen.set( event.item.idAgendamiento );
      this.position.set( event.coords );
      this.actions.set( event.item.actions );
      return;
    }
    this.isMenuOpen.set( '' );
    this.position.set( null );
    this.actions.set( null );
  }

  public async openHistory360(): Promise< void > {

    if ( !this.dataHistorial360() ) return;

    this.matDialog.open( ModalHistory360Component, {
      data: this.dataHistorial360(),
      maxWidth: '900px',
      minWidth: '400px',
      minHeight: '400px',
    });
  }

  public onUpdatedPage( event: number ) {
    this.page.set( event );
    this.getPatientHistory( true );
  }

  capitalizeType( name: string ): string {
    return this.utils.capitalizeString( name );
  }

}
